import { useEffect, useState, useRef } from "react";
import zoomSdk from "@zoom/appssdk";
import AppLayout from "@amzn/meridian/esm/components/app-layout";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Masthead from "@amzn/meridian/esm/components/masthead";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import {
  AIMS_NOT_AVAILABLE_TITLE,
  AIMS_STATUS,
  FEATURE_FLAGS,
  ROLES,
  SUMMARIZE_STATES,
  VIEWS,
  SESSION_TABS,
  PREFERENCE_SAVE_ERROR_MESSAGE,
  PANEL_WIDTH,
  AIMS_FLOWS,
  AUTO_START_MODES,
  MAIN_CONTEXT,
  TRANSCRIPT_NOT_AVAILABLE_MESSAGE,
} from "../../constants";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";
import NotAvailable from "../../components/NotAvailable";
import LogManager from "../../utils/logManager";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import ProductFeedback from "../../components/feedback/ProductFeedback";
import { FEEDBACK_PAGE } from "../../constants";

// Sub-components
import Footer from "./Footer";
import SessionHeader from "../session/SessionHeader";
import ErrorModals from "./ErrorModals";
import DefaultView from "./DefaultView";
import SessionView from "../session/SessionView";
import ErrorToaster from "../../components/error/ErrorToaster";
import WaveformLayout from "../../components/waveformLayout/WaveformLayout";
import RtmsRejected from "./RtmsRejected";
import Summaries from "../summaries/Summaries";
import SummaryDetail from "../summaries/SummaryDetail";
import Settings from "../settings/Settings";
import { useAgenticExtensions, useRealtimeStores, useSummarizationController, useStartedByStatus, useHomeNavigation } from "./home.hooks";

// Utils
import { useMeeting } from "../../context/MeetingContext";
import { usePreference } from "../../context/PreferenceContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { SessionProvider } from "../../context/SessionContext";
import useAutoStartSummarization from "../../hooks/useAutoStartSummarization";
import { updateSummaryReviewers } from "../../utils/summaryReviewerUtils";
import { ATTENDEE_FLOW_TEXT, FLOW_TEXT } from "./home.constants";

/**
 * Home - Main container for the in-meeting assistant UI.
 *
 * This component is responsible for:
 * - State management for the in-meeting assistant feature
 * - Routing to appropriate sub-views based on session state
 * - Rendering the footer navigation (End, Summaries, Settings)
 *
 * Serves both the summary and transcribe flows (meetingMode.aimsFlow); they share the session
 * machine and screens, and differ in who can start, auto-start, and screen copy (FLOW_TEXT).
 * Attendees don't know the flow (no availability call), so they get flow-neutral copy
 * (ATTENDEE_FLOW_TEXT).
 *
 * Sub-components:
 * - DefaultView: Shared view for initial state and session ended (with/without Start button)
 * - SessionView: Active session with tabs (Assistant, Agenda, Action Items); with Assistant off
 *   the session shows only a status line
 *
 * The RTMS/summarization state machine lives in useSummarizationController (home.hooks.js).
 *
 * @see hooks/useInMeetingInitialization.js for SDK init and AIMS availability
 * @see home.utils.js for RTMS session control
 */
const Home = () => {
  const featureFlags = useFeatureFlags();
  const { isLoaded: prefsLoaded, initialAutoStart } = usePreference();

  const {
    role,
    alias,
    meetingUuid,
    meetingId,
    appContext,
    aimsStatus,
    aimsNotAvailableMessage,
    hasGermanyAttendee,
    autoStartMode,
    inviteeAliases,
    externalInvitees,
    isAutostarting,
    initialSession,
    meetingMode,
  } = useMeeting();

  const isTranscribe = meetingMode.aimsFlow === AIMS_FLOWS.TRANSCRIPT;
  const flowText = role === ROLES.ATTENDEE
    ? ATTENDEE_FLOW_TEXT
    : FLOW_TEXT[isTranscribe ? AIMS_FLOWS.TRANSCRIPT : AIMS_FLOWS.SUMMARY];
  const { isAssistantEnabled } = featureFlags;

  // Summary distribution state
  const [sendToAllAttendees, setSendToAllAttendees] = useState(true);

  // Toast queue shared by all sources (agentic agent failures, preference-save failures, …).
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const emitToast = (message) =>
    setToasts((prev) => prev.concat({ id: `toast-${++toastIdRef.current}`, message }));

  // Chatbot state
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);

  // The RTMS/summarization state machine: summarize + rtms-error state, the session/timer refs,
  // the status-change listener, and the start()/end() actions.
  const {
    summarizeState,
    isRtmsStartedByOther,
    rtmsRequesterName,
    meetingAgendaActive,
    meetingActionItemsActive,
    rtmsStreamId,
    summarizeClickCount,
    isProductFeedbackModalOpen,
    setIsProductFeedbackModalOpen,
    errorModals,
    rtmsStartTimestampRef,
    start: startSummarization,
    end: endSummarization,
  } = useSummarizationController({
    meetingUuid,
    meetingId,
    role,
    alias,
    appContext,
    isTranscribe,
    isPerHost: isTranscribe && autoStartMode === AUTO_START_MODES.MULTIPLE,
    isAutostarting,
    initialSession,
  });

  // Session identity + viewer alias + live RTMS status shared with the active-session/summary
  // subtree via SessionContext, so SessionView and its descendants read them with useSession()
  // instead of drilling props.
  const sessionValue = {
    role,
    meetingUuid,
    meetingId,
    rtmsStreamId,
    alias,
    rtmsStartTime: rtmsStartTimestampRef.current,
    rtmsRequesterName,
    isRtmsStartedByOther,
    meetingAgendaActive,
    meetingActionItemsActive,
  };

  // View-navigation state machine (which view, selected summary, active session tab + handlers).
  const {
    view,
    setView,
    selectedTab,
    setSelectedTab,
    selectedSummary,
    handleSummariesClick,
    handleSelectSummary,
    handleBackToSummaries,
    handleSettingsClick,
    handleTabClick,
  } = useHomeNavigation();

  // Real-time layer: single WebSocket + the published-content and insight stores it feeds,
  // plus the on-session-start seed of action items + agenda.
  const { publishedContentStore, insightStore } = useRealtimeStores({
    meetingUuid,
    isAssistantEnabled,
    summarizeState,
    meetingAgendaActive,
    meetingActionItemsActive,
  });

  // "Started by" status when another host started the session.
  const { startedBy, dismissStartedBy } = useStartedByStatus({
    summarizeState,
    isRtmsStartedByOther,
    rtmsRequesterName,
    description: flowText.STARTED,
  });

  // Reset the transient chat/tab state at each new SUMMARIZING episode. Action items are stored
  // per meetingUuid and insightStore lives for the whole Home mount (meetingUuid is stable across
  // a stop/restart), so we intentionally do NOT clear them at the session boundary — a restart
  // keeps the still-valid rows visible instead of wiping them and re-fetching. A genuinely
  // different meeting is a fresh mount with an empty store. A new session starts on the default
  // Assistant tab — don't carry over a tab (e.g. Action Items) selected in the previous session.
  useEffect(() => {
    if (summarizeState === SUMMARIZE_STATES.SUMMARIZING) {
      setChatMessages([]);
      setIsChatLoading(false);
      publishedContentStore.clear();
      setSelectedTab(SESSION_TABS.ASSISTANT);
    }
  }, [summarizeState]);

  const handleSendToAllChange = async (checked) => {
    setSendToAllAttendees(checked);
    const success = await updateSummaryReviewers({
      rtmsStreamId,
      meetingUuid,
      meetingId,
      alias,
      checked,
    });
    if (!success) {
      setSendToAllAttendees(!checked);
      return;
    }
    if (hasGermanyAttendee) {
      try {
        const message = checked
          ? "You'll receive a meeting summary when it's ready. Sharing enabled."
          : "Only organizer will receive summary. Sharing disabled. Contact them for a copy or take notes.";
        await zoomSdk.sendMessageToChat({ message });
      } catch (error) {
        LogManager.error("Error sending summary sharing message to chat:", error);
      }
    }
  };

  const handleEndClick = () => {
    setView(VIEWS.MAIN);
    endSummarization();
  };

  // Auto-start summarization
  const { isAutoStarting, autoStartFailed, dismissAutoStartFailed, autoStartCompleted } = useAutoStartSummarization({
    role,
    aimsStatus,
    summarizeState,
    meetingUuid,
    appContext,
    onSummarize: () => startSummarization(),
  });

  // Agentic extension catalog + enabled-agent set: fetch, scheduled auto-activate, toggle,
  // and the partial-failure / all-inactive toasts (surfaced through emitToast).
  const {
    availableExtensions,
    enabledAgentIds,
    agentsLoading,
    applyActiveAgents,
    onToggleAgent,
    onPopoverOpen,
  } = useAgenticExtensions({
    isAssistantEnabled,
    role,
    meetingUuid,
    meetingId,
    rtmsStreamId,
    summarizeState,
    emitToast,
  });

  const isHost = role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST;

  // Transcribe: organizers / alternative hosts in a TranscriptAvailable meeting; everyone else
  // sees "not available". Summary: alternative hosts can't start when the summary goes to the
  // organizer only.
  const canStartSummarization = isTranscribe
    ? aimsStatus === AIMS_STATUS.TRANSCRIPT_AVAILABLE && isHost
    : role === ROLES.ORGANIZER || (role === ROLES.ALTERNATIVE_HOST && aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER);

  const isNotAvailable = aimsStatus === AIMS_STATUS.NOT_AVAILABLE || (isTranscribe && !canStartSummarization);
  const notAvailableMessage = isTranscribe ? TRANSCRIPT_NOT_AVAILABLE_MESSAGE : aimsNotAvailableMessage;

  // Check if End button should be visible (only for Organizer and Alternative Host when summarizing, and they started it)
  const showEndButton = isHost &&
                        summarizeState === SUMMARIZE_STATES.SUMMARIZING &&
                        !isRtmsStartedByOther;

  // Check if send-to-all toggle should be visible (organizer/alt host only)
  const showSendToAll = FEATURE_FLAGS.ALLOW_SUMMARY_DISTRIBUTION_TOGGLE &&
    !isTranscribe &&
    isHost &&
    !isRtmsStartedByOther &&
    !!rtmsStreamId &&
    aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER;

  // Check if session is active (summarizing)
  const isSessionActive = summarizeState === SUMMARIZE_STATES.SUMMARIZING;

  // Check if RTMS is loading (starting or stopping)
  const isRtmsLoading = summarizeState === SUMMARIZE_STATES.LOADING;

  // Show loading eagerly when auto-start is expected to fire — avoids relying on async state update timing
  const isAutoStartPending = prefsLoaded &&
    initialAutoStart === true &&
    role === ROLES.ORGANIZER &&
    (aimsStatus === AIMS_STATUS.AVAILABLE || aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) &&
    summarizeState === SUMMARIZE_STATES.DEFAULT &&
    !!appContext &&
    !autoStartFailed &&
    !autoStartCompleted;

  return (
    <Theme palette={aimsTheme}>
      {/* Centered like HIRE; the feedback popover anchors to this column too. */}
      <Column alignmentHorizontal="center" spacing="none">
        <AppLayout
          headerComponent={Masthead}
          footerComponent={Footer}
          backgroundColor="primary"
        >
          {/* Session Tabs Header - only visible during active session */}
          {isSessionActive && isAssistantEnabled && (
            <SessionHeader
              view={view}
              selectedTab={selectedTab}
              onTabClick={handleTabClick}
              insightStore={insightStore}
            />
          )}

          {/* Content Area */}
          <Box width={PANEL_WIDTH}>
            {view === VIEWS.SUMMARIES ? (
              <Summaries onSelectSummary={handleSelectSummary} isSessionActive={isSessionActive} />
            ) : view === VIEWS.SUMMARY_DETAIL ? (
              <SummaryDetail summary={selectedSummary} onBack={handleBackToSummaries} isSessionActive={isSessionActive} />
            ) : view === VIEWS.SETTINGS ? (
              <Settings
                isVerboseSummariesEnabled={featureFlags?.isVerboseSummariesEnabled}
                onBack={() => setView(VIEWS.MAIN)}
                onPreferenceSaveError={() => emitToast(PREFERENCE_SAVE_ERROR_MESSAGE)}
              />
            ) : isNotAvailable ? (
              <NotAvailable title={AIMS_NOT_AVAILABLE_TITLE} message={notAvailableMessage} />
            ) : summarizeState === SUMMARIZE_STATES.REJECTED ? (
              <RtmsRejected rtmsRequesterName={rtmsRequesterName} message={flowText.REJECTED} />
            ) : (isRtmsLoading || isAutoStarting || isAutoStartPending) ? (
              <WaveformLayout icon={<Loader size="medium" />} />
            ) : isSessionActive && !isAssistantEnabled ? (
              // Assistant off (country-blocked): no tabs, just the status
              <WaveformLayout>
                <Text type="h50">{flowText.ACTIVE}</Text>
              </WaveformLayout>
            ) : isSessionActive ? (
              <SessionProvider value={sessionValue}>
                <SessionView
                  selectedTab={selectedTab}
                  startedBy={startedBy}
                  dismissStartedBy={dismissStartedBy}
                  chatMessages={chatMessages}
                  setChatMessages={setChatMessages}
                  isChatLoading={isChatLoading}
                  setIsChatLoading={setIsChatLoading}
                  enabledAgentIds={enabledAgentIds}
                  availableExtensions={availableExtensions}
                  onAgentsRefreshed={applyActiveAgents}
                  publishedContentStore={publishedContentStore}
                  insightStore={insightStore}
                  inviteeAliases={inviteeAliases}
                  externalInvitees={externalInvitees}
                />
              </SessionProvider>
            ) : (
              <DefaultView
                summarizeState={summarizeState}
                summarizeClickCount={summarizeClickCount}
                onStart={startSummarization}
                flowText={flowText}
                showStartButton={canStartSummarization}
                showInformModal={!isTranscribe}
              />
            )}
          </Box>
          <Footer
            showEndButton={showEndButton}
            onEndClick={handleEndClick}
            onSummariesClick={handleSummariesClick}
            onSettingsClick={handleSettingsClick}
            onOpenGeneral={() => setView(VIEWS.SETTINGS)}
            activeView={view}
            showSettingsPopover={isSessionActive}
            showSendToAll={showSendToAll}
            sendToAllAttendees={sendToAllAttendees}
            onSendToAllAttendeesChange={handleSendToAllChange}
            // Agents are an Assistant feature
            showAgentToggle={isAssistantEnabled}
            canToggleAgents={role === ROLES.ORGANIZER}
            enabledAgentIds={enabledAgentIds}
            availableExtensions={availableExtensions}
            agentsLoading={agentsLoading}
            onPopoverOpen={isAssistantEnabled ? onPopoverOpen : undefined}
            onToggleAgent={onToggleAgent}
          />
        </AppLayout>
        <ProductFeedback
          isModalOpen={isProductFeedbackModalOpen}
          setIsModalOpen={setIsProductFeedbackModalOpen}
          usePopover={true}
          page={FEEDBACK_PAGE.SUMMARIZE_END}
        />
      </Column>
      <ErrorToaster
        toasts={toasts}
        onCloseToast={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
      />
      {/* Error Modals */}
      <ErrorModals {...errorModals} context={isTranscribe ? MAIN_CONTEXT.TRANSCRIBE : MAIN_CONTEXT.SUMMARIZE} />
      <AlertModal
        variant={ALERT_VARIANT.AUTO_START_FAILED}
        isOpen={autoStartFailed}
        onCancel={dismissAutoStartFailed}
        onStart={() => {
          dismissAutoStartFailed();
          startSummarization();
        }}
      />
    </Theme>
  );
};

export default Home;
